<template>
  <div class="modal" ref="modalRef" data-bs-backdrop="static" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">
            Change parents
          </h3>
          <button type="button" class="btn-close" v-bind="{ disabled: busyRef }" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div>
            Select which parent to change:
          </div>
          <div v-for="r in parentRoles" :key="r.role" class="parent">
            <label class="form-label">{{ r.label }}</label>
            <PersonCard
              :person-id="getSpouseId(r.role)"
              layout="small" 
              show-profile-type 
              show-profile-count="multiple" 
              static/>
            <div class="actions">
              <button type="button" class="btn btn-inline btn-link btn-sm" @click="onChange(r.role)">
                Change
              </button>
              <button type="button" v-if="getSpouseId(r.role)" class="btn btn-inline btn-link" @click="onChange(r.role, true)">
                Remove
              </button>
            </div>
          </div>
          <!-- <div class="alert alert-primary alert-sm">
             This will only change the identity of the child's parents.
             It will not change the parents' relationship with each other.
          </div> -->
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-primary" :disabled="busyRef" data-bs-dismiss="modal">
            Done
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal-body {
  padding: 0 2rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.alert {
  margin: 0;
}

.parent {
  margin-left: 1rem;

  .actions {
    margin-top: 0.25rem;
    display: flex;
    gap: 0.5rem;
    font-size: 0.875rem;
  }
}

.modal-footer button {
  min-width: 100px;
}

</style>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { FamilyRelationshipRole, ViewFamily } from '@/rd/ResearchDataModel'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { ProfileUtils } from './ProfileUtils'
import { Modal } from 'bootstrap'
import PersonCard from '@/manage/PersonCard.vue'
import { hideModalAsync } from '@/util/AppUtil'

const emit = defineEmits<{
  (e: 'change', role: FamilyRelationshipRole): void
  (e: 'remove', role: FamilyRelationshipRole): void
}>()

defineExpose({
  open,
})

const parentRoles = [
  { role: FamilyRelationshipRole.Father, label: "Father" },
  { role: FamilyRelationshipRole.Mother, label: "Mother" },
]

const childIdRef = ref<string>()
const parentFamilyRef = ref<ViewFamily>()
const busyRef = ref(false)
const modalRef = ref<HTMLElement>()

const viewPersonStore = useViewPersonStore()
const dataGroupStore = useDataGroupStore()

const viewPersonRef = computed(() => viewPersonStore.getLoadedPerson(childIdRef.value))
const profileIdsRef = computed(() => {
  if (!viewPersonRef.value)
    return []
  const matchDataGroups = dataGroupStore.getLoadedGroupList(viewPersonRef.value?.groupIds)
  return ProfileUtils.getNonPlaceholderIds(viewPersonRef.value.matchIds, matchDataGroups)
})

function open(childId: string, parentFamily: ViewFamily) {
  if (modalRef.value) {
    childIdRef.value = childId
    parentFamilyRef.value = parentFamily
    Modal.getOrCreateInstance(modalRef.value)?.show()
  }
}

function getSpouseId(role: FamilyRelationshipRole) {
  return parentFamilyRef.value?.getSpouseId(role)
}

async function onChange(role: FamilyRelationshipRole, remove = false) {
  if (modalRef.value) {
    await hideModalAsync(modalRef.value) // async so another modal can open immediately after
    if (remove)
      emit('remove', role)
    else
      emit('change', role)
  }
}
</script>
